import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FolderOpen, Upload, FileText, Search, Trash2, Plus, Edit2, File, Presentation, Table2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILE_ICONS = { PDF: FileText, DOCX: FileText, DOC: FileText, PPTX: Presentation, PPT: Presentation, XLSX: Table2, XLS: Table2, TXT: File, CSV: File };

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function KnowledgeHub() {
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [user, setUser] = useState(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editFolder, setEditFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({ name: '', description: '' });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '' });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const isAdmin = user?.role === 'admin';

  const fetchFolders = async () => {
    try {
      const res = await axios.get(`${API}/knowledge-hub/folders`, { headers: getHeaders() });
      setFolders(res.data);
    } catch (e) { toast.error('Failed to fetch folders'); }
  };

  const fetchDocuments = async () => {
    try {
      let url = `${API}/knowledge-hub/documents`;
      const params = [];
      if (selectedFolder) params.push(`folder_id=${selectedFolder}`);
      if (searchText) params.push(`search=${encodeURIComponent(searchText)}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setDocuments(res.data);
    } catch (e) { toast.error('Failed to fetch documents'); }
  };

  useEffect(() => { fetchFolders(); }, []);
  useEffect(() => { fetchDocuments(); }, [selectedFolder, searchText]);

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      if (editFolder) {
        await axios.put(`${API}/knowledge-hub/folders/${editFolder.id}`, folderForm, { headers: getHeaders() });
        toast.success('Folder updated');
      } else {
        await axios.post(`${API}/knowledge-hub/folders`, folderForm, { headers: getHeaders() });
        toast.success('Folder created');
      }
      setFolderDialogOpen(false); setFolderForm({ name: '', description: '' }); setEditFolder(null);
      fetchFolders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder and all its documents?')) return;
    try {
      await axios.delete(`${API}/knowledge-hub/folders/${folderId}`, { headers: getHeaders() });
      toast.success('Folder deleted');
      if (selectedFolder === folderId) setSelectedFolder(null);
      fetchFolders(); fetchDocuments();
    } catch (e) { toast.error('Failed to delete folder'); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error('Select a file'); return; }
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const params = new URLSearchParams();
      if (selectedFolder) params.append('folder_id', selectedFolder);
      if (uploadForm.title) params.append('title', uploadForm.title);
      if (uploadForm.description) params.append('description', uploadForm.description);
      await axios.post(`${API}/knowledge-hub/documents?${params.toString()}`, fd, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setUploadOpen(false); setUploadFile(null); setUploadForm({ title: '', description: '' });
      fetchDocuments(); fetchFolders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/knowledge-hub/documents/${docId}`, { headers: getHeaders() });
      toast.success('Document deleted'); fetchDocuments(); fetchFolders();
    } catch (e) { toast.error('Failed to delete document'); }
  };

  const currentFolderName = selectedFolder ? folders.find(f => f.id === selectedFolder)?.name : 'All Documents';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="knowledge-hub-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Knowledge Hub</h1>
          <p className="text-slate-600">Shared documents, presentations & resources</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button data-testid="create-folder-btn" variant="outline" onClick={() => { setEditFolder(null); setFolderForm({ name: '', description: '' }); setFolderDialogOpen(true); }} className="border-blue-200 text-blue-700 hover:bg-blue-50">
              <Plus size={16} className="mr-2" />New Folder
            </Button>
          )}
          <Button data-testid="upload-doc-btn" onClick={() => setUploadOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
            <Upload size={16} className="mr-2" />Upload Document
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex items-end gap-4">
        <div className="flex-1">
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search Documents</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-kb" placeholder="Search by title, description, filename..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="h-9 pl-9 rounded border-blue-200 text-sm" />
          </div>
        </div>
        {searchText && <Button variant="ghost" size="sm" onClick={() => setSearchText('')} className="text-slate-500 text-xs">Clear</Button>}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar - Folders */}
        <div className="col-span-12 lg:col-span-3">
          <div className="bg-white border border-blue-100 rounded shadow-sm p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-3">Folders</p>
            <button data-testid="folder-all" onClick={() => setSelectedFolder(null)} className={`w-full text-left px-3 py-2 rounded text-sm font-medium mb-1 transition-colors ${!selectedFolder ? 'bg-blue-50 text-blue-900 font-bold' : 'text-slate-600 hover:bg-slate-50'}`}>
              <FolderOpen size={14} className="inline mr-2" />All Documents
            </button>
            {folders.map(f => (
              <div key={f.id} className={`group flex items-center rounded mb-1 transition-colors ${selectedFolder === f.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                <button data-testid={`folder-${f.id}`} onClick={() => setSelectedFolder(f.id)} className={`flex-1 text-left px-3 py-2 text-sm font-medium ${selectedFolder === f.id ? 'text-blue-900 font-bold' : 'text-slate-600'}`}>
                  <FolderOpen size={14} className="inline mr-2" />{f.name}
                  <span className="ml-2 text-xs text-slate-400">({f.document_count})</span>
                </button>
                {isAdmin && (
                  <div className="hidden group-hover:flex items-center gap-0.5 pr-1">
                    <button onClick={() => { setEditFolder(f); setFolderForm({ name: f.name, description: f.description }); setFolderDialogOpen(true); }} className="p-1 text-slate-400 hover:text-blue-600"><Edit2 size={12} /></button>
                    <button onClick={() => handleDeleteFolder(f.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={12} /></button>
                  </div>
                )}
              </div>
            ))}
            {folders.length === 0 && <p className="text-xs text-slate-400 px-3 py-2">No folders yet</p>}
          </div>
        </div>

        {/* Documents */}
        <div className="col-span-12 lg:col-span-9">
          <div className="bg-white border border-blue-100 rounded shadow-sm">
            <div className="p-4 border-b border-blue-100 flex items-center justify-between">
              <p className="font-bold text-blue-900">{currentFolderName}</p>
              <span className="text-xs text-slate-500">{documents.length} documents</span>
            </div>
            {documents.length === 0 ? (
              <div className="p-12 text-center">
                <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No documents found</p>
                <p className="text-xs text-slate-400 mt-1">Upload a document to get started</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {documents.map(doc => {
                  const Icon = FILE_ICONS[doc.file_type] || File;
                  return (
                    <div key={doc.id} data-testid={`doc-${doc.id}`} className="p-4 flex items-center gap-4 hover:bg-blue-50 transition-colors">
                      <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Icon size={20} className="text-blue-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <a href={`${BACKEND_URL}${doc.file_url}`} target="_blank" rel="noreferrer" className="font-semibold text-blue-900 hover:text-blue-600 hover:underline text-sm truncate block">{doc.title}</a>
                        {doc.description && <p className="text-xs text-slate-500 truncate mt-0.5">{doc.description}</p>}
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span>{doc.file_type}</span>
                          <span>{formatSize(doc.file_size)}</span>
                          <span>by {doc.uploaded_by_name}</span>
                          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {(isAdmin || doc.uploaded_by === user?.id) && (
                        <Button size="sm" variant="ghost" data-testid={`delete-doc-${doc.id}`} onClick={() => handleDeleteDoc(doc.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50"><Trash2 size={14} /></Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Folder Dialog */}
      <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">{editFolder ? 'Edit Folder' : 'Create Folder'}</DialogTitle>
            <DialogDescription>Organize documents into folders</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateFolder} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Folder Name</Label>
              <Input data-testid="folder-name-input" value={folderForm.name} onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Description</Label>
              <textarea data-testid="folder-desc-input" value={folderForm.description} onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Optional description..." /></div>
            <Button type="submit" data-testid="submit-folder-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">{editFolder ? 'Save Changes' : 'Create Folder'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Upload Document</DialogTitle>
            <DialogDescription>Share a file with the team{selectedFolder ? ` in "${currentFolderName}"` : ''}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Title</Label>
              <Input data-testid="doc-title-input" value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} className="mt-2 rounded border-blue-200" placeholder="Optional - defaults to filename" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Description</Label>
              <textarea data-testid="doc-desc-input" value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Brief description of the document..." /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">File (PDF, DOCX, PPTX, XLSX, TXT, CSV)</Label>
              <Input data-testid="doc-file-input" type="file" accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.txt,.csv" onChange={(e) => setUploadFile(e.target.files[0] || null)} required className="mt-2 rounded border-blue-200 text-sm" />
              {uploadFile && <p className="text-xs text-emerald-600 mt-1">Selected: {uploadFile.name}</p>}
            </div>
            <Button type="submit" data-testid="submit-upload-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Upload</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
